import { useEffect, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { callPost } from '@/api/fetcher';
import { selectAccountSlug } from '@/redux/session/selectors';
import {
  MergedTelnyxNumber,
  TelnyxVoiceIntegration,
  VoiceCredentialStatus,
} from '@/shared/types/integration';

import useDebounce from './useDebounce';
import { useDeskId } from './useDeskId';

const endpoints = {
  phones: (slug: string, deskId: string, integrationId: string) =>
    `/www/api/integrations/telnyx-voice/${slug}/desks/${deskId}/integrations/${integrationId}/phones`,
};

/** Validates the Telnyx API key and fetches available phone numbers. */
export const useTelnyxVoice = (integrationId: string) => {
  const deskId = useDeskId();
  const slug = useSelector(selectAccountSlug);

  const { control } = useFormContext<TelnyxVoiceIntegration>();
  const formApiKey = useWatch({ control, name: 'config.api_key' });

  const debouncedApiKey = useDebounce(formApiKey, 1000);

  const [apiKeyStatus, setApiKeyStatus] = useState<VoiceCredentialStatus>(
    VoiceCredentialStatus.UNVERIFIED
  );
  const [lastAttemptedApiKey, setLastAttemptedApiKey] = useState<string | null>(null);
  const [phones, setPhones] = useState<MergedTelnyxNumber[] | null>(null);

  const validateCredentialsMutation = useMutation({
    mutationFn: async ({ api_key }: { api_key: string }) => {
      return callPost(endpoints.phones(slug, deskId, integrationId), { api_key });
    },
    onSuccess: (data) => {
      setPhones(data);
      setApiKeyStatus(VoiceCredentialStatus.VERIFIED);
    },
    onError: () => {
      setPhones(null);
      setApiKeyStatus(VoiceCredentialStatus.INVALID);
    },
  });

  useEffect(() => {
    if (!debouncedApiKey) {
      setApiKeyStatus(VoiceCredentialStatus.UNVERIFIED);
      setPhones(null);
      setLastAttemptedApiKey(null);
      return;
    }

    if (lastAttemptedApiKey === debouncedApiKey) {
      return;
    }

    setLastAttemptedApiKey(debouncedApiKey);
    setApiKeyStatus(VoiceCredentialStatus.VALIDATING);
    validateCredentialsMutation.mutate({ api_key: debouncedApiKey });
    // Excluding validateCredentialsMutation — its reference changes every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationId, slug, deskId, debouncedApiKey, lastAttemptedApiKey]);

  return { apiKeyStatus, phones, verificationStatus: apiKeyStatus };
};
